import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db, DexieDraft } from '@/shared/db/dexieDb';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { Language } from '@/shared/types';

export type { DexieDraft };

export function useDrafts(clientId?: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['drafts', user?.id, clientId],
    queryFn: async (): Promise<DexieDraft[]> => {
      if (!user) return [];
      let query = db.drafts.where('owner_id').equals(user.id);
      const all = await query.toArray();
      if (clientId) return all.filter(d => d.client_id === clientId);
      return all.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    },
    enabled: !!user,
  });
}

export function useDraftsForTemplate(templateId: string, clientId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['drafts-template', user?.id, templateId, clientId],
    queryFn: async (): Promise<DexieDraft[]> => {
      if (!user) return [];
      const all = await db.drafts.where('owner_id').equals(user.id).toArray();
      return all.filter(d =>
        d.template_id === templateId &&
        (clientId ? d.client_id === clientId : true)
      ).sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    },
    enabled: !!user,
  });
}

export function useSaveDraft() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draft: {
      id?: string;
      template_id: string; template_name: string;
      client_id: string | null; client_name: string;
      field_values: Record<string, string>;
      checkbox_values: Record<string, boolean>;
      step: number; language: Language;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const now = new Date().toISOString();
      const id = draft.id || crypto.randomUUID();
      await db.drafts.put({
        id, owner_id: user.id,
        template_id: draft.template_id,
        template_name: draft.template_name,
        client_id: draft.client_id,
        client_name: draft.client_name,
        field_values: draft.field_values,
        checkbox_values: draft.checkbox_values,
        step: draft.step,
        language: draft.language,
        created_at: now,
        updated_at: now,
      });
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }),
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await db.drafts.delete(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }),
  });
}
